// WebsitePopup.js
import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';

// --- Icons for a polished look ---
import LanguageIcon from '@mui/icons-material/Language'; // Globe Icon

// --- Styled Components ---
// These remain the same to keep the visual style
const ModalOverlay = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(10, 22, 41, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
        'from': { opacity: 0 },
        'to': { opacity: 1 },
    },
});

const ModalPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    maxWidth: '500px',
    position: 'relative',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    animation: 'slideUp 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
    '@keyframes slideUp': {
        'from': { transform: 'translateY(20px)', opacity: 0 },
        'to': { transform: 'translateY(0)', opacity: 1 },
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '60px',
    height: '60px',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    borderRadius: '50%',
    margin: '0 auto 1rem auto',
}));

const InlineSpinner = () => <CircularProgress size={24} color="inherit" />;

// --- The Main Component ---
// The 'onClose' prop is removed as this modal cannot be closed by the user directly.
const WebsitePopup = ({ onSubmit }) => {
    const [webname, setWebname] = useState('');
    const [submissionError, setSubmissionError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setSubmissionError(''); // Clear previous errors

        try {
            // The onSubmit prop is called with the website name, which can be an empty string.
            // The parent component must handle the logic of what to do next.
            await onSubmit(webname);
            // On success, the parent component is responsible for hiding this modal.
        } catch (err) {
            console.error("Submission failed in WebsitePopup:", err);
            setSubmissionError(err.message || "An unexpected error occurred. Please try again.");
            setIsLoading(false); // Stop loading only on error to allow retry.
        }
    };

    // The button is only disabled while the submission is in progress.
    const isSubmitDisabled = isLoading;

    return (
        // The overlay no longer has an onClick handler.
        <ModalOverlay>
            {/* Stop propagation is no longer needed but is harmless to keep. */}
            <ModalPaper elevation={4} onClick={(e) => e.stopPropagation()}>
                <Box textAlign="center" mt={2}>
                    <IconWrapper>
                        <LanguageIcon fontSize="large" />
                    </IconWrapper>
                    <Typography variant="h5" component="h2" fontWeight="600" gutterBottom>
                        Enter Your Website (Optional)
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        We can analyze your site to help configure your chatbot, or you can skip this step.
                    </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        fullWidth
                        id="website-address"
                        label="Website Address"
                        name="website"
                        autoComplete="url"
                        placeholder="e.g., www.yourcompany.com"
                        value={webname}
                        onChange={(e) => setWebname(e.target.value)}
                        // The error prop is now only used for submission errors, not validation.
                        error={!!submissionError}
                        helperText={submissionError}
                        autoFocus
                        disabled={isLoading}
                         sx={{
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none', // Removes the default outline border
                            },
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        {/* The "Back" button has been removed. */}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isSubmitDisabled}
                        >
                            {isLoading ? <InlineSpinner /> : 'Continue'}
                        </Button>
                    </Box>
                </Box>
            </ModalPaper>
        </ModalOverlay>
    );
};

export default WebsitePopup;